import { useState } from "react";
import * as api from "../../api";
import { saveEntry as saveCredentialEntry } from "../../lib/credentialHistory";

/* F3B-6b — Shopping flow (selección de paquetes, credenciales, verificación
 * de jugador y carrito) extraído VERBATIM desde App.js.
 * selectedGame NO vive aquí: se inyecta (lo escribe también el games-poll).
 * cart/setCart se inyectan (compartidos con el submit de pago). */
export function useShoppingFlow({ selectedGame, cart, setCart, showToast, effectiveUsd, usdToBs, exchangeRate, setExchangeRate }) {
  const [selectedPackage, setSelectedPackage] = useState(null);
  // Packages the user has pre-selected for the current game (multi-select, before entering credentials)
  const [pendingPkgs, setPendingPkgs] = useState([]);
  // Whether the "Paso 2 · Datos de cuenta" modal is open (only after user clicks Realizar Pago)
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  // Restricted-purchase confirmation modal (e.g. Pase de Nivel — one-time buy per account)
  const [pendingVerifyPkg, setPendingVerifyPkg] = useState(null); // full pkg object awaiting acknowledgement
  const [verifyAcknowledged, setVerifyAcknowledged] = useState(false);
  const [playerId, setPlayerId] = useState("");
  const [playerPassword, setPlayerPassword] = useState("");
  const [playerServer, setPlayerServer] = useState("");
  const [accessMethod, setAccessMethod] = useState(""); // For games with hasAccessMethod = true (CoD)
  const [verifiedPlayerName, setVerifiedPlayerName] = useState(null);
  const [verifiedPlayerRegion, setVerifiedPlayerRegion] = useState(null);
  const [isVerifyingId, setIsVerifyingId] = useState(false);
  const [idVerificationStatus, setIdVerificationStatus] = useState("idle");
  // Free Fire flow: after a successful lookup we stage the result here
  // until the user confirms "¿Eres tú?" via the inline confirmation box.
  const [ffPendingConfirmation, setFfPendingConfirmation] = useState(null);
  // Bump this counter whenever we persist a credential entry so the
  // <CredentialHistoryChips> row re-reads localStorage and re-renders.
  const [credHistoryRefreshKey, setCredHistoryRefreshKey] = useState(0);

  const handleVerifyPlayerId = async () => {
    const uid = playerId.trim();
    if (!uid) { showToast("Error", "Ingresa un ID válido", "error"); return; }

    setIsVerifyingId(true);
    setIdVerificationStatus("idle");
    setVerifiedPlayerName(null);
    setVerifiedPlayerRegion(null);
    setFfPendingConfirmation(null);

    // ---- Roblox: username-based, no external verification available ----
    if (selectedGame?.slug === "roblox") {
      setTimeout(() => {
        setIsVerifyingId(false);
        if (uid.length >= 3) {
          setVerifiedPlayerName(uid);
          setIdVerificationStatus("success");
          showToast("¡Usuario Verificado!", `Cuenta Roblox: @${uid}`, "success");
        } else {
          setIdVerificationStatus("error");
          showToast("Error", "Nombre muy corto", "error");
        }
      }, 400);
      return;
    }

    // ---- Free Fire: full verification (Smile.one) + BR block ----
    if (selectedGame?.slug === "ff" || selectedGame?.slug === "free-fire" || selectedGame?.slug === "freefire") {
      try {
        const res = await api.verifyFreeFirePlayerFull(uid);
        setIsVerifyingId(false);

        // Provider outage → soft-verify. Frontend allows Añadir al Carrito
        // with a warning; admin will validate the ID manually.
        if (res?.fallback) {
          setVerifiedPlayerName(uid);
          setVerifiedPlayerRegion(null);
          setFfPendingConfirmation(null);
          setIdVerificationStatus("warning");
          showToast(
            "Verificación no disponible",
            res.message || "No pudimos verificar tu Nickname en este momento. Asegúrate de que el ID esté correcto antes de pagar.",
            "info",
          );
          return;
        }

        // Success with nickname → stage for user confirmation. We do NOT
        // set status to "success" yet — the user must click "Sí, continuar
        // al pago" in the confirmation box.
        if (res?.success && res?.nickname) {
          setFfPendingConfirmation({
            uid,
            nickname: res.nickname,
            region: res.region || "US/SAC",
          });
          setVerifiedPlayerName(null);
          setVerifiedPlayerRegion(null);
          setIdVerificationStatus("pending_confirmation");
          return;
        }

        // Shouldn't happen, but treat unexpected body as an error.
        setFfPendingConfirmation(null);
        setVerifiedPlayerName(null);
        setVerifiedPlayerRegion(null);
        setIdVerificationStatus("error");
        showToast("Verificación no disponible", "Respuesta inesperada del servidor. Intenta nuevamente.", "error");
      } catch (err) {
        setIsVerifyingId(false);
        setFfPendingConfirmation(null);
        setVerifiedPlayerName(null);
        setVerifiedPlayerRegion(null);
        setIdVerificationStatus("error");
        // HTTP 400 covers: invalid UID format AND region_br_blocked. The
        // backend puts a descriptive message in err.message; surface it
        // as-is so the user knows exactly why.
        const msg = err.status === 400
          ? (err.message || "El ID no es válido.")
          : "No pudimos verificar el ID. Intenta nuevamente.";
        const title = /brasil|BR\b/i.test(err.message || "")
          ? "Región no soportada"
          : "ID inválido";
        showToast(title, msg, "error");
      }
      return;
    }

    // ---- Other id-based games: fallback local sanity check. Delta Force
    // usa IDs numéricos largos (hasta 20 dígitos); el resto conserva su rango.
    setTimeout(() => {
      setIsVerifyingId(false);
      const isDeltaForce = /delta[\s_-]*force/i.test(selectedGame?.slug || "")
        || /delta[\s_-]*force/i.test(selectedGame?.name || "");
      const validId = isDeltaForce ? /^\d{6,20}$/.test(uid) : /^\d{6,12}$/.test(uid);
      if (validId) {
        setVerifiedPlayerName(uid);
        setIdVerificationStatus("success");
        showToast("¡ID Verificado!", `Jugador: ${uid}`, "success");
      } else {
        setIdVerificationStatus("error");
        showToast("ID inválido", isDeltaForce ? "El ID de Delta Force debe tener entre 6 y 20 dígitos" : "El ID debe tener entre 6 y 12 dígitos", "error");
      }
    }, 400);
  };

  // ============ CART ============
  const handleAddToCart = async () => {
    if (!selectedGame || pendingPkgs.length === 0 || !playerId.trim()) return;
    const ct = selectedGame.credentialType || "id";
    const isPwGame = ct === "username_password";
    const isServerGame = ct === "id_server";
    const isEmailGame = ct === "email";
    const isPhoneGame = ct === "phone";
    if (isPwGame && !playerPassword) {
      showToast("Faltan Datos", "Debes ingresar la contraseña del juego", "error");
      return;
    }
    if (isServerGame && !playerServer.trim()) {
      showToast("Faltan Datos", "Debes ingresar el ID del servidor", "error");
      return;
    }
    if (isPwGame && selectedGame.hasAccessMethod && !accessMethod) {
      showToast("Faltan Datos", `Selecciona la Forma de Acceso (${(selectedGame.accessMethods || []).join(" / ")})`, "error");
      return;
    }
    if (isEmailGame && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(playerId.trim())) {
      showToast("Correo inválido", "Escribe un correo electrónico válido", "error");
      return;
    }
    if (isPhoneGame && !/^[0-9+\-\s]{7,20}$/.test(playerId.trim())) {
      showToast("Teléfono inválido", "Escribe un número de teléfono válido", "error");
      return;
    }
    let referralQuotes = [];
    try {
      const quoted = await api.quoteReferralPrices(pendingPkgs.map((pkg) => ({
        gameId: selectedGame.id, packageId: pkg.id, playerId: playerId.trim(),
      })), exchangeRate);
      referralQuotes = quoted?.items || [];
      const confirmedRate = Number(quoted?.exchange_rate);
      if (Number.isFinite(confirmedRate) && confirmedRate > 0 && Math.abs(confirmedRate - Number(exchangeRate || 0)) >= 0.01) {
        setExchangeRate?.(confirmedRate);
        showToast("Tasa actualizada", "La tasa cambió antes de agregar el paquete. El carrito refleja la cotización vigente.", "info");
      }
    } catch (err) {
      showToast("No se pudo validar el precio", err.message || "Intenta nuevamente.", "error");
      return;
    }
    const newItems = pendingPkgs.map((pkg) => {
      const quote = referralQuotes.find((q) => q.gameId === selectedGame.id && q.packageId === pkg.id);
      const priceUsd = Number(quote?.price_usd ?? effectiveUsd(pkg));
      return ({
      tempId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${pkg.id}`,
      gameId: selectedGame.id,
      gameName: selectedGame.name,
      gameImage: selectedGame.image,
      packageId: pkg.id,
      packageName: pkg.name,
      priceUsd,
      priceBs: Number(quote?.price_bs ?? usdToBs(priceUsd)),
      normalPriceUsd: Number(quote?.normal_usd ?? effectiveUsd(pkg)),
      referralDiscountUsd: Number(quote?.discount_usd || 0),
      credentialType: ct,
      playerId: playerId.trim(),
      playerPassword: isPwGame ? playerPassword : null,
      playerServer: isServerGame ? playerServer.trim() : null,
      accessMethod: isPwGame && selectedGame.hasAccessMethod ? accessMethod : null,
      verifiedPlayerName: verifiedPlayerName || null,
    });
    });
    setCart((previous) => [...(previous || []), ...newItems]);
    // Persist this account to the client-side credential history so the
    // shopper can one-tap reuse it next time (localStorage only — never
    // leaves the browser). Keyed by (gameId, credentialType), max 5 entries.
    try {
      saveCredentialEntry(selectedGame.id, ct, {
        playerId: playerId.trim(),
        playerPassword: isPwGame ? (playerPassword || "") : undefined,
        playerServer: isServerGame ? (playerServer || "").trim() : undefined,
        accessMethod: isPwGame && selectedGame.hasAccessMethod ? accessMethod : undefined,
        name: verifiedPlayerName || undefined,
      });
      setCredHistoryRefreshKey((k) => k + 1);
    } catch (e) {
      console.warn("[credHistory] save on add-to-cart failed:", e);
    }
    showToast(
      pendingPkgs.length === 1 ? "Agregado al Carrito" : `${pendingPkgs.length} paquetes agregados`,
      `${selectedGame.name} · ${pendingPkgs.map(p => p.name).join(", ")}`,
      "success"
    );
    // Reset selection to allow adding more
    setPendingPkgs([]);
    setCheckoutOpen(false);
    setSelectedPackage(null);
    setPlayerId("");
    setPlayerPassword("");
    setPlayerServer("");
    setAccessMethod("");
    setVerifiedPlayerName(null);
    setVerifiedPlayerRegion(null);
    setFfPendingConfirmation(null);
    setIdVerificationStatus("idle");
  };

  const handleRemoveFromCart = (tempId) => {
    setCart(cart?.filter(i => i.tempId !== tempId));
  };

  return {
    selectedPackage, setSelectedPackage, pendingPkgs, setPendingPkgs,
    checkoutOpen, setCheckoutOpen, pendingVerifyPkg, setPendingVerifyPkg,
    verifyAcknowledged, setVerifyAcknowledged,
    playerId, setPlayerId, playerPassword, setPlayerPassword,
    playerServer, setPlayerServer, accessMethod, setAccessMethod,
    verifiedPlayerName, setVerifiedPlayerName,
    verifiedPlayerRegion, setVerifiedPlayerRegion,
    isVerifyingId, idVerificationStatus, setIdVerificationStatus,
    ffPendingConfirmation, setFfPendingConfirmation,
    credHistoryRefreshKey, setCredHistoryRefreshKey,
    handleVerifyPlayerId, handleAddToCart, handleRemoveFromCart,
  };
}
